import DesignTokens from '@tableau/design-tokens';
import { GrantPermissionsDialog, IShareContentRecipient } from '@tableau/share-dialog';
import { GrantPermissionsDialogProps } from '@tableau/share-dialog/lib/src/components/grantAccess/GrantPermissionsDialog';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, TabStyles } from '@tableau/widgets';
import React from 'react';

import { ModalInstance, ReactModalOptions, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { IToastOptions, ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Set } from '~commonInfra/utils/Set';
import { setExplicitPermissionsForGrantees } from '~permissions/requests/ExplicitPermissions';
import { ICapabilityValue } from '~permissions/requests/types/ICapabilityValue';
import { EditPermissionsAction } from '~permissions/services/EditPermissionsAction';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { setCollectionVisibility } from '~src/Collections/actions/SetCollectionVisibilityAction';
import { CollectionVisibility } from '~src/Collections/space/CollectionModels';
import { CollectionsDialogStyles } from '~src/Collections/space/CollectionsDialogStyles';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { interpolateString } from '~src/commonInfra/utils/StringInterpolation';
import { ITypedId, ITypedIdWithName } from '~src/httpClient/serverApi/types/ITypedId';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { AuthorizableType } from '~src/permissions/types/AuthorizableType';
import { CapabilitySectionsByAuthorizableType } from '~src/permissions/types/CapabilitySections';
import { PermissionRoleName } from '~src/permissions/types/PermissionRole';
import { PermissionRoleHelper } from '~src/permissions/utils/PermissionRoleHelper';
import { PermissionsHelper } from '~src/permissions/utils/PermissionsHelper';
import { sendGrantAccessDialogClickedTelemetry, sendManagePermissionsClickedTelemetry } from '~src/requestAccess/telemetry/RequestAccessTelemetry';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ExplorableItemType, ExplorableItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { applyColor }from '~src/style/ColorService';


export namespace GrantPermissionsAfterSharingDialog {
  const DIALOG_WIDTH = 500;
  export const DIALOG_TESTID = 'collection-grant-access';
  export const MANAGE_PERMISSIONS_BUTTON_TESTID = DIALOG_TESTID + '-manage-permissions';
  export const DISMISS_BUTTON_TESTID = DIALOG_TESTID + '-dismiss';
  export const GRANT_ACCESS_BUTTON_TESTID = DIALOG_TESTID + '-grant-access';

  export interface ContentsProps {
    content: IContentInfo<ExplorableItemType>;
    shareRecipients: IShareContentRecipient[];
    unpermissionedRecipients: ITypedId[];
    sourceAuthorizable?: ITypedIdWithName;
    isRequestAccessDialogEnabled?: boolean;
    requesterName?: string;
    requesterUsername?: string;
  }

  export const enum DialogResult {
    Dismiss = 'dismiss',
    GrantPermissions = 'grant',
    ManagePermissions = 'managePermissions'
  }

  interface MessagesForGrantPermissionsAfterSharing {
    shareOneUser: (a: { CONTENT_NAME: string }) => string;
    shareMultipleUsers: (a: { CONTENT_NAME: string, TOTAL_USER_COUNT: number, TOTAL_USER_COUNT_LOC: string, UNPERMISSIONED_USER_COUNT: number, UNPERMISSIONED_USER_COUNT_LOC: string }) => string;
    shareOneUserLocked?: (a: { CONTENT_NAME: string }) => string;
    shareOneUserLockedWorkbook?: (a: { CONTENT_NAME: string }) => string;
    shareMultipleUsersLocked?: (a: { CONTENT_NAME: string, TOTAL_USER_COUNT: number, TOTAL_USER_COUNT_LOC: string, UNPERMISSIONED_USER_COUNT: number, UNPERMISSIONED_USER_COUNT_LOC: string }) => string;
    shareMultipleUsersLockedWorkbook?: (a: { CONTENT_NAME: string, TOTAL_USER_COUNT: number, TOTAL_USER_COUNT_LOC: string, UNPERMISSIONED_USER_COUNT: number, UNPERMISSIONED_USER_COUNT_LOC: string }) => string;
    permissionsTemplate?: () => string;
    permissionsActionOneUser?: () => string;
    permissionsActionMultipleUsers?: () => string;
    dialogHeader?: (a: { CONTENT_NAME: string }) => string;
    dialogHeaderLocked?: (a: { PROJECT_NAME: string }) => string;
    dialogHeaderLockedWorkbook?: (a: { WORKBOOK_NAME: string }) => string;
    dialogTitle: () => string;
    grantAccessMessage: (a: { USER_FRIENDLY_NAME: string, USERNAME: string, CONTENT_NAME: string }) => string;
  }

  const ContentTypeToMessagesMap = new Map<ExplorableItemType, MessagesForGrantPermissionsAfterSharing>()
    .set(ExplorableItemTypeValue.collection, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_collection,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_collection,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_collection,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.datasource, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_datasource,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_datasource,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_datasource,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_datasource,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_datasource,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_datasource,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_datasource,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_datasource,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.flow, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_flow,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_flow,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_flow,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_flow,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_flow,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_flow,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_flow,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_flow,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.virtualConnection, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_virtualConnection,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_virtualConnection,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_virtualConnection,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_virtualConnection,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_virtualConnection,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_virtualConnection,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_virtualConnection,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_virtualConnection,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.project, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_project,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_project,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_project,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_project,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_project,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_project,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_project,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_project,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.view, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_view,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_view,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_view,
      shareOneUserLockedWorkbook: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedWorkbook_view,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_view,
      shareMultipleUsersLockedWorkbook: Messages.shareGrantPermissionsDialogMessage_lockedWorkbook_view,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_view,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_view,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_view,
      dialogHeaderLockedWorkbook: Messages.shareGrantPermissionDialogHeader_lockedWorkbook_view,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_view,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.workbook, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_workbook,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_workbook,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_workbook,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_workbook,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_workbook,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_workbook,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_workbook,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_workbook,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    });

  const ContentTypeToTemplateType = new Map<ExplorableItemType, AuthorizableType>([
    [ExplorableItemTypeValue.datasource, AuthorizableType.PROJECT_DATASOURCE_TEMPLATE],
    [ExplorableItemTypeValue.flow, AuthorizableType.PROJECT_FLOW_TEMPLATE],
    [ExplorableItemTypeValue.virtualConnection, AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE],
    [ExplorableItemTypeValue.project, AuthorizableType.PROJECT],
    [ExplorableItemTypeValue.view, AuthorizableType.PROJECT_WORKBOOK_TEMPLATE],
    [ExplorableItemTypeValue.workbook, AuthorizableType.PROJECT_WORKBOOK_TEMPLATE],
  ]);
  const popToast = (toast: IToastOptions): void => {
    const service = ToasterService.instance;
    service.pop(toast);
  };

  const toastHandler = (message: string, isInfo: boolean): void => {
    popToast({
      text: message,
      type: isInfo ? ToastType.Success : ToastType.Error,
    });
};

  const renderBoldLockedProject: () => JSX.Element = () => (
    <span style={{ fontWeight: 'bold', }}>
      {Messages.shareGrantPermissionsDialogMessageLockedPermissions()}
    </span>
  );

  const renderBoldTabbedWorkbook: () => JSX.Element = () => (
    <span style={{ fontWeight: 'bold', }}>
      {Messages.shareGrantPermissionsDialogMessageTabbedViews()}
    </span>
  );

  export const Contents = (props: ContentsProps) => {
    if (FeatureFlags.grantPermissionsAfterSharing && props.content.contentType !== 'collection') {
      let grantPermissionHeader;
      let grantPermissionMessage;
      if (props.shareRecipients.length === 1) {
        if (props.sourceAuthorizable === undefined) {
          grantPermissionHeader = ContentTypeToMessagesMap.get(props.content.contentType).dialogHeader({CONTENT_NAME: props.content.name});
          if(props.isRequestAccessDialogEnabled) {
            grantPermissionMessage = ContentTypeToMessagesMap.get(props.content.contentType).grantAccessMessage({USER_FRIENDLY_NAME: props.requesterName, USERNAME: props.requesterUsername, CONTENT_NAME: props.content.name});
          } else {
            grantPermissionMessage = ContentTypeToMessagesMap.get(props.content.contentType).shareOneUser({CONTENT_NAME: props.content.name});
          }
        } else if (props.sourceAuthorizable.type === 'project') {
          grantPermissionHeader = ContentTypeToMessagesMap.get(props.content.contentType).dialogHeaderLocked({PROJECT_NAME: props.sourceAuthorizable.name});
          if(props.isRequestAccessDialogEnabled) {
            grantPermissionMessage = ContentTypeToMessagesMap.get(props.content.contentType).grantAccessMessage({USER_FRIENDLY_NAME: props.requesterName, USERNAME: props.requesterUsername, CONTENT_NAME: props.content.name});
          } else {
            grantPermissionMessage = interpolateString(ContentTypeToMessagesMap.get(props.content.contentType).shareOneUserLocked({CONTENT_NAME: props.content.name}), {LOCKED_PERMISSIONS: renderBoldLockedProject});
          }
        } else if (props.sourceAuthorizable.type === 'workbook') {
          grantPermissionHeader = ContentTypeToMessagesMap.get(props.content.contentType).dialogHeaderLockedWorkbook({WORKBOOK_NAME: props.sourceAuthorizable.name});
          if(props.isRequestAccessDialogEnabled) {
            grantPermissionMessage = ContentTypeToMessagesMap.get(props.content.contentType).grantAccessMessage({USER_FRIENDLY_NAME: props.requesterName, USERNAME: props.requesterUsername, CONTENT_NAME: props.content.name});
          } else {
            grantPermissionMessage = interpolateString(ContentTypeToMessagesMap.get(props.content.contentType).shareOneUserLockedWorkbook({CONTENT_NAME: props.content.name}), {TABBED_VIEWS: renderBoldTabbedWorkbook});
          }
        }
      } else {
        if (props.sourceAuthorizable === undefined) {
          grantPermissionHeader = ContentTypeToMessagesMap.get(props.content.contentType).dialogHeader({CONTENT_NAME: props.content.name});
          grantPermissionMessage = ContentTypeToMessagesMap.get(props.content.contentType).shareMultipleUsers({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length),
          });
        } else if (props.sourceAuthorizable.type === 'project') {
          grantPermissionHeader = ContentTypeToMessagesMap.get(props.content.contentType).dialogHeaderLocked({PROJECT_NAME: props.sourceAuthorizable.name});
          grantPermissionMessage = interpolateString(ContentTypeToMessagesMap.get(props.content.contentType).shareMultipleUsersLocked({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length),
          }), {LOCKED_PERMISSIONS: renderBoldLockedProject});
        } else if (props.sourceAuthorizable.type === 'workbook') {
          grantPermissionHeader = ContentTypeToMessagesMap.get(props.content.contentType).dialogHeaderLockedWorkbook({WORKBOOK_NAME: props.sourceAuthorizable.name});
          grantPermissionMessage = interpolateString(ContentTypeToMessagesMap.get(props.content.contentType).shareMultipleUsersLockedWorkbook({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length),
          }), {TABBED_VIEWS: renderBoldTabbedWorkbook});
        }
      }
      return (
        <div style={{ fontWeight: 'normal', fontSize: '12px', lineHeight: '18px', color: applyColor(DesignTokens.color.font.default.secondary, '#4F4F4F'), letterSpacing: '0.2px' }}>
          <h3 style={{ fontWeight: 'normal', color:applyColor(DesignTokens.color.font.default.default,'#000000'), marginTop: '0'}}>{grantPermissionHeader}</h3>
          {grantPermissionMessage}
          {
            <div>
              <br/>
              {ContentTypeToMessagesMap.get(props.content.contentType).permissionsTemplate()}
            </div>
          }
        </div>
      );
    }
    return (
      <div style={{ fontWeight: 'normal', fontSize: '12px', lineHeight: '18px', color: applyColor(DesignTokens.color.font.default.secondary, '#4F4F4F'), letterSpacing: '0.2px' }}>
        {props.shareRecipients.length === 1 &&
          Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_collection({
            CONTENT_NAME: props.content.name
          })
        }
        {props.shareRecipients.length !== 1 &&
          Messages.shareGrantPermissionsDialogMessage_collection({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
          })}
      </div>
    );
  };

  interface FooterProps {
    onManagePermissions: () => void;
    onDismiss: () => void;
    onGrantAccess: () => void;
  }
  export const Footer = (props: FooterProps) => {
    return (
      <div data-tb-test-id={`${DIALOG_TESTID}-footer`} className={CollectionsDialogStyles.GrantPermissionsDialogFooter}>
        <span className={CollectionsDialogStyles.GrantPermissionsManagePermissionsButton}>
          <ButtonWidget
            handleClick={props.onManagePermissions}
            buttonType={ButtonType.LowEmphasis}
            children={Messages.shareGrantPermissionsDialogManagePermissionsAction()}
            testId={MANAGE_PERMISSIONS_BUTTON_TESTID}
          />
        </span>
        <span className={CollectionsDialogStyles.GrantPermissionsRightButtons}>
          <div style={{paddingLeft: TabStyles.Spacing.MediumSpace}}>
            <ButtonWidget
              handleClick={props.onDismiss}
              buttonType={ButtonType.Outline}
              children={Messages.shareGrantPermissionsDialogDismiss()}
              testId={DISMISS_BUTTON_TESTID}
            />
          </div>
          <div style={{paddingLeft: TabStyles.Spacing.MediumSpace}}>
            <ButtonWidget
              handleClick={props.onGrantAccess}
              buttonType={ButtonType.Primary}
              children={Messages.shareGrantPermissionsDialogGrantAccess()}
              testId={GRANT_ACCESS_BUTTON_TESTID}
            />
          </div>
        </span>
      </div>);
  };

  export function Options(
    footerProps: FooterProps,
    contentProps: ContentsProps,
    capabilities: ICapabilityValue[],
    grantAccessHandler: () => void): ReactModalOptions {
      if (FeatureFlags.sharePermissionsVizclientEnabled) {

        const content = contentProps.content;
        let sourceAuthorizable;
        if (contentProps.sourceAuthorizable !== undefined) {
          sourceAuthorizable = {
            id: contentProps.sourceAuthorizable.id,
            type: contentProps.sourceAuthorizable.type,
            name: contentProps.sourceAuthorizable.name,
          };
        }
        const dialogProps: GrantPermissionsDialogProps = {
          content: {
            id: content.id,
            name: content.name,
            type: content.contentType,
          },
          capabilities,
          userRequestedAccess: contentProps.isRequestAccessDialogEnabled,
          requesterName: contentProps.requesterName,
          shareRecipients: contentProps.shareRecipients,
          sourceAuthorizable,
          unpermissionedRecipients: contentProps.unpermissionedRecipients,
          managePermissionsHandler: footerProps.onManagePermissions,
          grantAccessHandler,
          dismissHandler: footerProps.onDismiss,
          toastHandler,
        };
        return {
          title: ContentTypeToMessagesMap.get(contentProps.content.contentType).dialogTitle(),
          testId: DIALOG_TESTID,
          type: DialogType.NormalWithTitleBar,
          showCloseButton: true,
          positioningBehavior: DialogPositioningBehavior.Centered,
          dialogWidth: DIALOG_WIDTH,
          dialogBodyStyle: { maxWidth: DIALOG_WIDTH + 'px' },
          dialogContent: React.createElement(GrantPermissionsDialog, dialogProps),
        };
      } else {
        return {
          title: ContentTypeToMessagesMap.get(contentProps.content.contentType).dialogTitle(),
          testId: DIALOG_TESTID,
          type: DialogType.NormalWithTitleBar,
          showCloseButton: true,
          positioningBehavior: DialogPositioningBehavior.Centered,
          dialogWidth: DIALOG_WIDTH,
          dialogBodyStyle: { maxWidth: DIALOG_WIDTH + 'px' },
          dialogContent: <Contents {...contentProps} />,
          dialogContentStyle: { padding: 0 },
          footerContent: <Footer {...footerProps} />
        };
      }
  }

  export function open(props: ContentsProps) {
    let modalInstance: ModalInstance<DialogResult>;

    let authorizable: ITypedId = { type: props.content.contentType, id: props.content.id };
    if (props.sourceAuthorizable !== undefined) {
      if (props.sourceAuthorizable.type === 'project') {
        authorizable = {type: ContentTypeToTemplateType.get(props.content.contentType), id: props.sourceAuthorizable.id };
      } else if (props.sourceAuthorizable.type === 'workbook') {
        authorizable = {type: props.sourceAuthorizable.type, id: props.sourceAuthorizable.id };
      }
    }
    const capabilitySections = CapabilitySectionsByAuthorizableType(
      getSessionManagementServiceInstance().editingFlowsEnabled(),
      FeatureFlags.explainDataPermissionEnabled,
      getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled(),
      FeatureFlags.allowNonAdminRefreshExtractsEnabled
    );
    const permissionRoleHelper = new PermissionRoleHelper(capabilitySections);
    const viewTemplate = PermissionsHelper.capabilityValuesFromPermissions(PermissionsHelper.IPermissionsFromPermissions(permissionRoleHelper.getPermissionsForTypeAndRole(authorizable.type, PermissionRoleName.View)));

    const onManagePermissions = (): void => {
      sendManagePermissionsClickedTelemetry(props.content.contentType);
      const contentAsSet = Set.singletonForIdItem(props.content);
      EditPermissionsAction.showEditPermissionsDialog(contentAsSet, props.content.contentType);
      modalInstance && modalInstance.close(DialogResult.ManagePermissions);
    };

    const onDismiss = (): void => {
      modalInstance && modalInstance.close(DialogResult.Dismiss);
    };

    const onGrant = () => {
      sendGrantAccessDialogClickedTelemetry(props.content.contentType);
      modalInstance?.close(DialogResult.GrantPermissions);
      // Due to slowness in BE updates we optimistically update collection visibility label in redux
      // after we've successfully updated permissions.
      // Reminder private just means there are no explicit permissions set.
      if (props.content.contentType === 'collection') {
        getAppStore().dispatch(setCollectionVisibility([props.content.id], CollectionVisibility.public));
      }
    };

    const onGrantAccess = () => {
      sendGrantAccessDialogClickedTelemetry(props.content.contentType);
      const onSuccess = () => {
        ToasterService.instance.pop({
          type: ToastType.Success,
          text: Messages.shareGrantPermissionsDialogGrantSuccessMsg({
            USER_COUNT: props.unpermissionedRecipients.length,
            USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
          })
        });

        // Due to slowness in BE updates we optimistically update collection visibility label in redux
        // after we've successfully updated permissions.
        // Reminder private just means there are no explicit permissions set.
        if (props.content.contentType === 'collection') {
          getAppStore().dispatch(setCollectionVisibility([props.content.id], CollectionVisibility.public));
        }
      };

      const showErrorToast = () => ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.shareGrantPermissionsDialogGrantErrorMsg({
          USER_COUNT: props.unpermissionedRecipients.length,
          USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
        })
      });

      setExplicitPermissionsForGrantees(authorizable, props.unpermissionedRecipients, viewTemplate)
        .then(rejectIfResultContainsError)
        .then(onSuccess, showErrorToast)
        .finally(() => modalInstance?.close(DialogResult.GrantPermissions));
    };

    modalInstance = ReactModalService.open(Options(
      { onManagePermissions, onDismiss, onGrantAccess },
      props,
      viewTemplate,
      onGrant,
    ));
    return modalInstance.result;
  }
}
